import Accordion from "@/common/components/Accordion";
import { buttonVariants } from "@/common/components/Button/styles";
import DropdownMenu from "@/common/components/DropdownMenu";
import Select from "@/common/components/Select";
import { Mapper } from "@/common/services/mapper";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import { cn } from "@/common/utils/cn";
import { useState } from "react";
import UpdateMaintenanceGeneralInformation from "./UpdateMaintenanceGeneralInformation";
import DeleteMaintenanceSpecialtyModal from "./DeleteMaintenanceSpecialty";
import CreateMaintenanceSpecialtyModal from "./CreateMaintenanceSpecialtyModal";

interface MaintenanceGeneralInformationSectionProps {
  establishmentCompany: EstablishmentCompanyData;
  disabled?: boolean;
}

const MaintenanceGeneralInformationSection = ({
  establishmentCompany,
  disabled,
}: MaintenanceGeneralInformationSectionProps) => {
  const [deleteIsOpen, setDeleteIsOpen] = useState(false);

  const maintenanceSpecialty = establishmentCompany?.maintenance_specialty;

  return (
    <Accordion
      defaultOpen
      head={(
        <div className="flex items-center gap-3">
          <div onClick={(event) => {
            event.stopPropagation();
          }}>
            <UpdateMaintenanceGeneralInformation
              establishmentCompany={establishmentCompany}
              disabled={disabled}
            />
          </div>

          <p className="text-lg font-bold">
            Informações gerais
          </p>
        </div>
      )}
      className="p-0"
      triggerClassName="p-4"
      bodyClassName="p-4 pt-0"
      body={(
        <div className="flex flex-col gap-4">
          <Select
            label="Infraestrutura para manutenção"
            isMulti
            isDisabled
            optionLabelFormat="icon"
            value={
              Mapper.mapToOptions<EstablishmentCompanyData['maintenance_infrastructures'][number], true, true>({
                labelFieldName: ['infrastructure', 'display_name'],
                valueFieldName: ['infrastructure', 'id'],
                nestedLabelField: true,
                nestedValueField: true,
              })(establishmentCompany?.maintenance_infrastructures || [])
            }
          />

          <Select
            label="Equipe técnica"
            isMulti
            isDisabled
            optionLabelFormat="icon"
            value={
              Mapper.mapToOptions<EstablishmentCompanyData['maintenance_technical_professionals'][number], true, true>({
                labelFieldName: ['technical_professional', 'display_name'],
                valueFieldName: ['technical_professional', 'id'],
                nestedLabelField: true,
                nestedValueField: true,
              })(establishmentCompany?.maintenance_technical_professionals || [])
            }
          />

          <div className="flex flex-col gap-2">
            <p className="text-base">
              Especialidade
            </p>

            {maintenanceSpecialty && (
              <div className="bg-white-100 p-4 rounded-lg flex items-center gap-2">
                <div className="flex items-center gap-2 flex-1">
                  <div className="bg-white-100 p-2 rounded-md">
                    <p className="text-xs font-bold">
                      {maintenanceSpecialty.system_part.system.display_name}
                    </p>
                  </div>

                  <div className="flex flex-col items-start">
                    <p className="font-bold">
                      {maintenanceSpecialty.action.display_name}

                      &nbsp;

                      <span className="font-normal">
                        em
                      </span>

                      &nbsp;

                      {maintenanceSpecialty.system_part.part.display_name}
                    </p>

                    <p className="text-xs">
                      {maintenanceSpecialty.action.description}
                    </p>
                  </div>
                </div>

                <DropdownMenu
                  triggerClassName={cn(buttonVariants({ variant: 'neutral' }))}
                  menuPositionAlign="end"
                  triggerLabel="Opções"
                  disabled={disabled}
                  options={[
                    {
                      label: 'Remover',
                      onClick: () => setDeleteIsOpen(true),
                      className: '*:text-danger-soft',
                      operationKlassToCheckPermission: 'Admin::Establishment::Companies::UpdateMaintenanceModels',
                    },
                  ]}
                />

                <DeleteMaintenanceSpecialtyModal
                  establishmentCompany={establishmentCompany}
                  maintenanceSpecialtyId={maintenanceSpecialty.id}
                  isOpen={deleteIsOpen}
                  setIsOpen={setDeleteIsOpen}
                />
              </div>
            )}

            {!maintenanceSpecialty && (
              <CreateMaintenanceSpecialtyModal
                establishmentCompany={establishmentCompany}
                disabled={disabled}
              />
            )}
          </div>
        </div>
      )}
    />
  );
}

export default MaintenanceGeneralInformationSection;
